import { Add } from '@mui/icons-material';
import { Button, CircularProgress, FormGroup, FormLabel, Grid, Stack } from '@mui/material';

import CustomTextValidator from 'common/components/CustomTextValidators';
import ImageContainer from 'common/components/ImageContainer';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import RenderFormExtraFields from 'common/features/renderForms/RenderFormExtraFields';
import { getDynamicFields, getNestedData } from 'common/features/renderForms/utils';
import { useAbortController } from 'common/hooks';
import { useFormFields } from 'libs/hooksLib';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteAsset,
    getAssets,
    getAssetsUploading,
    resetAssets,
    setAssets,
    uploadAssetAction,
} from 'redux/slices/assetSlice';
import ImageFileInput from '../../../common/components/ImageFileInput';
import useStylesProductForm from '../styles/useStylesProductForm.styles';
import ProductFormCommon from './ProductFormCommon';

const ProductFormVariants = (props) => {
    const { variant, setVariants, close, index, readOnly, formConfigProductVariant, canEdit } = props;

    const { t } = useTranslation();

    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesProductForm();
    const classes = { ...classes1, ...classes2 };

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const assets = useSelector(getAssets);
    const uploading = useSelector(getAssetsUploading);
    const getSignal = useAbortController();

    const initialVarianForm = useMemo(() => {
        const initial = {
            externalId: variant.externalId,
            url: variant.url,
            images: variant.images,
            currency: variant.currency,
            price: variant.price,
            discountType: variant.discountType,
            discount: variant.discount,
            stock: variant.stock,
            selectedOptions: variant.selectedOptions,
            sku: variant.sku,
            gtin: variant.gtin,
            broken: variant?.broken ?? false,
            brokenUrl: variant?.broken ? variant?.url : undefined,
            extraFields: variant?.extraFields,
        };
        const dynamic = canEdit ? getDynamicFields(formConfigProductVariant, variant?.extraFields) : {};
        return { ...initial, ...dynamic };
    }, [variant, formConfigProductVariant, canEdit]);

    const [fields, handleFieldChange] = useFormFields(initialVarianForm);

    const handleAddVariant = (event) => {
        event.preventDefault();
        if (validateForm()) {
            setVariants({
                url: fields.url,
                externalId: fields.externalId,
                images: assets,
                currency: fields.currency,
                price: fields.price,
                discountType: fields.discountType,
                discount: fields.discount,
                stock: fields.stock,
                selectedOptions: fields.selectedOptions,
                sku: fields.sku,
                gtin: fields.gtin,
                extraFields:
                    canEdit && formConfigProductVariant
                        ? getNestedData(formConfigProductVariant, fields)
                        : fields.extraFields,
            });

            dispatch(resetAssets());
            close();
        }
    };

    const validateForm = () => {
        if (!assets || assets.length === 0) {
            enqueueSnackbar(t('product.productFormVariants.imageError'), {
                variant: 'error',
            });
            return false;
        }
        return true;
    };

    useEffect(() => {
        dispatch(setAssets(fields.images));
    }, [dispatch, fields.images]);

    function handleUploadFile(file) {
        dispatch(uploadAssetAction(file, getSignal()));
    }

    function handleError() {
        enqueueSnackbar(t('product.productFormVariants.formError'), {
            variant: 'error',
        });
    }

    const onDelete = (e, idx) => {
        e.preventDefault();
        dispatch(deleteAsset(idx));
    };

    return (
        <div key={index}>
            <ValidatorForm
                onSubmit={handleAddVariant}
                onError={handleError}
                className={cx(classes.form, classes.settingsForm)}
            >
                <ProductFormCommon readOnly={readOnly} fields={fields} handleFieldChange={handleFieldChange} />

                <FormGroup className={classes.formGroup}>
                    <FormLabel>SKU</FormLabel>
                    <CustomTextValidator
                        InputProps={{
                            readOnly: readOnly,
                        }}
                        name="sku"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        value={fields.sku}
                        onChange={handleFieldChange}
                    // validators={['required']}
                    // errorMessages={[t('product.productFormConfiguration.nameRequired')]}
                    />
                </FormGroup>

                <FormGroup className={classes.formGroup}>
                    <FormLabel>GTIN</FormLabel>
                    <CustomTextValidator
                        InputProps={{
                            readOnly: readOnly,
                        }}
                        name="gtin"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        value={fields.gtin}
                        onChange={handleFieldChange}
                    // validators={['required']}
                    // errorMessages={[t('product.productFormConfiguration.nameRequired')]}
                    />
                </FormGroup>

                <FormGroup className={classes.formGroup}>
                    <FormLabel>{t('product.productFormVariants.image')}</FormLabel>
                    <Grid container spacing={1} className={classes.imagesContainer}>
                        {assets.map((asset, idx) => (
                            <ImageContainer
                                key={idx}
                                asset={asset}
                                onDelete={
                                    readOnly
                                        ? (e) => {
                                            e.preventDefault();
                                        }
                                        : (e) => onDelete(e, idx)
                                }
                                deleteButton={!readOnly}
                                size={'big'}
                            />
                        ))}
                        {!readOnly && assets.length === 0 && (
                            <Grid item md={3}>
                                <ImageFileInput
                                    inputId={'raised-button-file'}
                                    uploadAssetFn={handleUploadFile}
                                    disabled={uploading}
                                />
                                <label htmlFor="raised-button-file">
                                    <Button
                                        component="span"
                                        className={cx(classes.uploadImageButton, classes.uploadImageButtonBig)}
                                    >
                                        {uploading && <CircularProgress />}
                                        {!uploading && (
                                            <>
                                                <Add />
                                                <span>{t('product.productFormVariants.upload')}</span>
                                            </>
                                        )}
                                    </Button>
                                </label>
                            </Grid>
                        )}
                    </Grid>
                    <span className={classes.imagesRecommendation}>
                        {t('product.productFormVariants.imageRecommend')}
                    </span>

                    {canEdit ? (
                        <Stack direction="column" mt={2}>
                            <RenderFormExtraFields
                                fields={fields}
                                handleFieldChange={handleFieldChange}
                                name="extraFields"
                                formConfig={formConfigProductVariant}
                            />
                        </Stack>
                    ) : null}
                </FormGroup>
                <Grid container spacing={1} className={cx(classes.optionsRow, classes.positionEnd)}>
                    <Grid item md={3}>
                        <Button
                            variant="contained"
                            size="small"
                            className={cx(classes.buttonStyle)}
                            disableElevation={true}
                            onClick={close}
                        >
                            {readOnly ? t('product.productForm.close') : t('product.productFormVariants.cancel')}
                        </Button>
                    </Grid>
                    {!readOnly && (
                        <Grid item md={3}>
                            <Button
                                variant="contained"
                                size="small"
                                type="submit"
                                color="primary"
                                className={cx(classes.buttonStyle, classes.saveButton)}
                                disableElevation={true}
                            >
                                {t('product.productFormVariants.save')}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </ValidatorForm>
        </div>
    );
};

export default ProductFormVariants;
